// Time formatting and date-preset math for the signed-out Events directory.
// Every function here is pure: the instant to measure from and the zone to
// read wall-clock time in are arguments, never the ambient clock or the
// browser's own zone.

import { instantToWallTimeInZone, wallTimeInZoneToInstant } from '../event-time';

/** An IANA zone the platform's Intl data knows. The `tz` URL param is parsed
 *  with this before any formatter sees it. */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch (error) {
    if (error instanceof RangeError) return false;
    throw error;
  }
}

/** "Nov 5 · 12:00 – 1:00 PM PST": the start date, then the time range with
 *  the zone named explicitly so a reader never has to guess whose clock the
 *  times are on. An event that ends on a later day spells out both dates
 *  ("Nov 5, 12:00 PM PST – Nov 6, 1:00 PM PST"). Ranges come from
 *  `formatRange`, so each locale gets its own dash, spacing and shared
 *  day-period handling. */
export function formatEventWhen(
  startsAt: string,
  endsAt: string,
  timeZone: string,
  locale: string,
): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const dayKey = (d: Date): string => instantToWallTimeInZone(d.toISOString(), timeZone).slice(0, 10);
  if (dayKey(start) !== dayKey(end)) {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).formatRange(start, end);
  }
  const date = new Intl.DateTimeFormat(locale, { timeZone, month: 'short', day: 'numeric' }).format(start);
  const times = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).formatRange(start, end);
  return `${date} · ${times}`;
}

/** The calendar block on a card: the start's month (upper-cased short name)
 *  and day of month, both read in `timeZone`. */
export function eventDateBlock(
  startsAt: string,
  timeZone: string,
  locale: string,
): { month: string; day: string } {
  const start = new Date(startsAt);
  return {
    month: new Intl.DateTimeFormat(locale, { timeZone, month: 'short' })
      .format(start)
      .toUpperCase(),
    day: new Intl.DateTimeFormat(locale, { timeZone, day: 'numeric' }).format(start),
  };
}

/** The zone's generic name in `locale` ("Pacific Time"), for a zone the
 *  offered list does not label. */
export function timeZoneDisplayName(timeZone: string, locale: string): string {
  const part = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'longGeneric' })
    .formatToParts(new Date(0))
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? timeZone;
}

export const PUBLIC_EVENT_DATE_PRESETS = [
  'today',
  'this_week',
  'this_weekend',
  'next_30_days',
] as const;

export type PublicEventDatePreset = (typeof PUBLIC_EVENT_DATE_PRESETS)[number];

const DAY_MS = 24 * 60 * 60 * 1000;

// Calendar-day arithmetic on a "YYYY-MM-DD" wall date, done in UTC so no
// zone's DST shift can move the date.
function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

// 0 = Monday … 6 = Sunday: the directory's weeks run Monday to Sunday.
function weekdayFromMonday(date: string): number {
  return (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
}

function startOfDay(date: string, timeZone: string): Date {
  return wallTimeInZoneToInstant(`${date}T00:00`, timeZone);
}

/**
 * The `from`/`to` instants (ISO, inclusive) a date preset asks the API for,
 * as calendar days in `timeZone` — the zone the page shows times in, so
 * "Today" means today on the clock the reader is reading:
 *
 * - today: the whole of today.
 * - this_week: today through Sunday (weeks run Monday–Sunday).
 * - this_weekend: Saturday and Sunday of this week; from today when today is
 *   already the weekend.
 * - next_30_days: today and the 30 days after it.
 *
 * Every range starts at a midnight, not at `now`, so an event under way today
 * still matches (the API keeps only events that have not ended), and the
 * range — and so the query key — holds still for the whole day.
 */
export function datePresetRange(
  preset: PublicEventDatePreset,
  now: Date,
  timeZone: string,
): { from: string; to: string } {
  const today = instantToWallTimeInZone(now.toISOString(), timeZone).slice(0, 10);
  const weekday = weekdayFromMonday(today);
  const range = (fromDay: string, lastDay: string) => ({
    from: startOfDay(fromDay, timeZone).toISOString(),
    // The last millisecond of `lastDay`: one before the next day's midnight.
    to: new Date(startOfDay(addDays(lastDay, 1), timeZone).getTime() - 1).toISOString(),
  });
  switch (preset) {
    case 'today':
      return range(today, today);
    case 'this_week':
      return range(today, addDays(today, 6 - weekday));
    case 'this_weekend':
      return range(weekday >= 5 ? today : addDays(today, 5 - weekday), addDays(today, 6 - weekday));
    case 'next_30_days':
      return range(today, addDays(today, 30));
  }
}
